import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@freelancer-os/ui/src/components/ui/card';
import { Button } from '@freelancer-os/ui/src/components/ui/button';
import { Badge } from '@freelancer-os/ui/src/components/ui/badge';
import { Input } from '@freelancer-os/ui/src/components/ui/input';
import { Label } from '@freelancer-os/ui/src/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@freelancer-os/ui/src/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@freelancer-os/ui/src/components/ui/dropdown-menu';
import { ChevronDown, Clock, MailPlus, Trash2, UserPlus, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { inviteMemberSchema } from '@freelancer-os/validation';
import { useAuthStore } from '../../../stores/authStore';
import {
  useMembers,
  useInvitations,
  useInviteMember,
  useUpdateMemberRole,
  useRemoveMember,
} from '../../../hooks/useTeam';
import type { MemberRole } from '@freelancer-os/shared-types';

const ROLE_STYLES: Record<string, string> = {
  OWNER: 'text-brand-700 border-brand-200 bg-brand-50',
  ADMIN: 'text-blue-700 border-blue-200 bg-blue-50',
  MEMBER: 'text-surface-600 border-surface-200 bg-surface-50',
};

export function TeamTab() {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const currentUserId = useAuthStore((s) => s.user?.id);
  const currentRole = useAuthStore((s) => s.role);
  const canManage = currentRole === 'OWNER' || currentRole === 'ADMIN';
  const isOwner = currentRole === 'OWNER';

  const { data: members = [], isLoading: membersLoading } = useMembers();
  const { data: invitations = [], isLoading: invitationsLoading } = useInvitations();
  const inviteMember = useInviteMember();
  const updateRole = useUpdateMemberRole();
  const removeMember = useRemoveMember();

  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'ADMIN' | 'MEMBER'>('MEMBER');
  const [inviteError, setInviteError] = useState('');

  function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    setInviteError('');

    const result = inviteMemberSchema.safeParse({ email: inviteEmail, role: inviteRole });
    if (!result.success) {
      setInviteError(result.error.issues[0]?.message ?? 'Validation error');
      return;
    }

    inviteMember.mutate(result.data, {
      onSuccess: () => {
        setShowInviteDialog(false);
        setInviteEmail('');
        setInviteRole('MEMBER');
      },
    });
  }

  function handleRoleChange(membershipId: string, role: MemberRole) {
    if (role === 'OWNER') return;
    updateRole.mutate({ id: membershipId, role: role as 'ADMIN' | 'MEMBER' });
  }

  // Pending (non-accepted) invitations
  const pendingInvitations = invitations.filter((inv) => !inv.acceptedAt);

  return (
    <div className="space-y-6">
      {/* Members */}
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-base">{t('team.title')}</CardTitle>
            <CardDescription className="mt-1">
              {t('team.memberCount', { count: members.length })}
            </CardDescription>
          </div>
          {canManage && (
            <Button
              size="sm"
              className="h-8 text-xs font-semibold"
              onClick={() => setShowInviteDialog(true)}
            >
              <UserPlus className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} />
              {t('team.invite')}
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {membersLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-14 bg-surface-100 animate-pulse rounded-lg" />
              ))}
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-10 h-10 text-surface-300 mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-sm text-surface-500">{t('team.empty')}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {members.map((member) => {
                const isSelf = member.user.id === currentUserId;
                const isMemberOwner = member.role === 'OWNER';
                const canChangeRole = isOwner && !isSelf && !isMemberOwner;
                const canRemove = canManage && !isSelf && !isMemberOwner;

                const initials = member.user.fullName
                  ? member.user.fullName
                      .split(' ')
                      .map((w) => w[0])
                      .slice(0, 2)
                      .join('')
                      .toUpperCase()
                  : '??';

                return (
                  <div
                    key={member.id}
                    className="flex items-center justify-between p-3 rounded-lg border border-surface-200 hover:bg-surface-50 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 bg-gradient-to-br from-brand-400 to-emerald-500 flex items-center justify-center text-white text-[11px] font-bold shrink-0">
                        {initials}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-surface-900 truncate">
                          {member.user.fullName || '—'}
                          {isSelf && (
                            <span className="ml-1.5 text-xs text-surface-400 font-normal">
                              {t('team.you')}
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-surface-400 truncate">{member.user.email}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {canChangeRole ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium border rounded-md hover:bg-surface-50 transition-colors">
                              <Badge variant="outline" className={ROLE_STYLES[member.role]}>
                                {tc(`roles.${member.role}`)}
                              </Badge>
                              <ChevronDown className="w-3 h-3 text-surface-400" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => handleRoleChange(member.id, 'ADMIN')}
                              className={member.role === 'ADMIN' ? 'font-semibold' : ''}
                            >
                              {tc('roles.ADMIN')}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleRoleChange(member.id, 'MEMBER')}
                              className={member.role === 'MEMBER' ? 'font-semibold' : ''}
                            >
                              {tc('roles.MEMBER')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <Badge variant="outline" className={ROLE_STYLES[member.role]}>
                          {tc(`roles.${member.role}`)}
                        </Badge>
                      )}

                      {canRemove && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-surface-400 hover:text-red-600 hover:bg-red-50"
                          onClick={() => removeMember.mutate(member.id)}
                          disabled={removeMember.isPending}
                        >
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.8} />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending Invitations */}
      {canManage && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('team.pendingTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            {invitationsLoading ? (
              <div className="h-12 bg-surface-100 animate-pulse rounded-lg" />
            ) : pendingInvitations.length === 0 ? (
              <div className="text-center py-6">
                <MailPlus className="w-8 h-8 text-surface-300 mx-auto mb-2" strokeWidth={1.5} />
                <p className="text-xs text-surface-400">{t('team.pendingEmpty')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {pendingInvitations.map((inv) => (
                  <div
                    key={inv.id}
                    className="flex items-center justify-between p-3 rounded-lg border border-dashed border-surface-200"
                  >
                    <div className="min-w-0">
                      <p className="text-sm text-surface-700 truncate">{inv.email}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge variant="outline" className={ROLE_STYLES[inv.role]}>
                          {tc(`roles.${inv.role}`)}
                        </Badge>
                        <span className="flex items-center gap-1 text-xs text-surface-400">
                          <Clock className="w-3 h-3" strokeWidth={1.8} />
                          {t('team.expires', {
                            date: new Date(inv.expiresAt).toLocaleDateString('vi-VN', {
                              day: '2-digit',
                              month: '2-digit',
                            }),
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('team.inviteTitle')}</DialogTitle>
            <DialogDescription>
              {t('team.inviteDescription')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInvite} className="space-y-4">
            <div>
              <Label htmlFor="invite-email" className="text-sm font-medium text-surface-700">
                {t('team.inviteEmail')}
              </Label>
              <Input
                id="invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder={t('team.inviteEmailPlaceholder')}
                className="mt-1.5"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-surface-700">{t('team.inviteRole')}</Label>
              <div className="mt-1.5 flex gap-2">
                <button
                  type="button"
                  onClick={() => setInviteRole('MEMBER')}
                  className={`flex-1 px-3 py-2 text-sm font-medium border rounded-md transition-colors ${
                    inviteRole === 'MEMBER'
                      ? 'border-brand-500 bg-brand-50 text-brand-700'
                      : 'border-surface-200 text-surface-600 hover:bg-surface-50'
                  }`}
                >
                  {tc('roles.MEMBER')}
                </button>
                <button
                  type="button"
                  onClick={() => setInviteRole('ADMIN')}
                  className={`flex-1 px-3 py-2 text-sm font-medium border rounded-md transition-colors ${
                    inviteRole === 'ADMIN'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-surface-200 text-surface-600 hover:bg-surface-50'
                  }`}
                >
                  {tc('roles.ADMIN')}
                </button>
              </div>
            </div>

            {(inviteError || inviteMember.error) && (
              <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2">
                <p className="text-sm text-red-700">
                  {inviteError || (inviteMember.error as Error).message}
                </p>
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowInviteDialog(false)}
              >
                {t('team.inviteCancel')}
              </Button>
              <Button type="submit" disabled={inviteMember.isPending} className="font-semibold">
                {inviteMember.isPending ? t('team.inviteSubmitting') : t('team.inviteSubmit')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
